import { Button, DownloadSVG } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import {
  TemplateDefinition,
  TemplateProps,
} from "@/components/AIComponents/ExportablesModal/templates/registry";
import { generatePDF } from "@/utils/generatePDF";
import { buttonContainerStyles } from "./DownloadPDFButton.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface DownloadPDFButtonProps {
  selectedExportable: TemplateDefinition<any> | null;
  formData: TemplateDefinition<any> | null;
}

const DownloadPDFButton = ({
  selectedExportable,
  formData,
}: DownloadPDFButtonProps) => {
  const handleDownload = async () => {
    if (!selectedExportable) return;

    try {
      const templateProps: TemplateProps = {
        formData: formData || null,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onChange: () => {},
        editMode: false,
      };

      const pdfBlob = await generatePDF(
        selectedExportable.Component,
        templateProps,
        {
          filename: `${selectedExportable.label ?? selectedExportable.title}.pdf`,
          format: "letter",
          orientation: selectedExportable.pdfOptions.orientation,
        },
      );

      // Create a URL for the blob and trigger download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedExportable.label ?? selectedExportable.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  return (
    <ButtonWithTrackEvent
      disabled={!selectedExportable}
      flavor={Button.flavors.primary}
      onClick={handleDownload}
      icon={{
        SvgComponent: DownloadSVG,
        alignment: Button.iconAlignments.right,
      }}
      __cssFor={{ root: buttonContainerStyles }}
      dataProps={{
        "data-qa-selector": `download-${selectedExportable?.label?.toLowerCase().replace(/\s+/g, "-") || "exportable"}-pdf`,
      }}
      objectType={objectTypes.generatedTextAsset}
      actionPrefix={actionPrefixes.initiate}
      actionName={actionNames.download}
      streamProperties={{
        description: "Download exportable PDF",
      }}
    >
      Download as a PDF
    </ButtonWithTrackEvent>
  );
};

export default DownloadPDFButton;
